import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, mergeProps as _mergeProps, openBlock as _openBlock, createBlock as _createBlock, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue";
const _hoisted_1 = { class: "px-5 pt-6 pb-4 flex flex-col flex-1 min-h-0" };
const _hoisted_2 = { class: "flex items-center justify-between mb-6 sm:mb-8" };
const _hoisted_3 = { class: "text-2xl sm:text-3xl font-bold text-gray-900 shrink-0 mr-60" };
const _hoisted_4 = { class: "flex items-center gap-3" };
const _hoisted_5 = { class: "shrink-0" };
const _hoisted_6 = {
    key: 0,
    class: "h-full flex flex-col"
};
import { Avatar, AdvancedFilterPopover, Button, DataTable, Pager, TableFilterSelector, TableShell, Tooltip, useTableQuery, useTableViews, } from "@eudonet/opal";
import { usePermissions, useListColumns, useListSettings, saveListNavContext } from "@/shared/composables";
import ModuleState from "@/shared/components/ModuleState.vue";
import ModuleLoading from "@/shared/components/ModuleLoading.vue";
import { INDUSTRY_SECTOR_FALLBACK } from "../../domain/industry-sector";
import { computed, h, onMounted, ref, watch } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useFilters } from "@/shared/composables/useFilters";
import { useQuickFilterCatalog, useQuickFilters } from "@/shared/composables/useQuickFilters";
import { getFilterMetadata } from "@/shared/infrastructure/api/filterMetadataApi";
import { useAdvancedFilter } from "@/shared/composables/useAdvancedFilter";
import { useAdvancedFilterLabels } from "@/shared/composables/useAdvancedFilterLabels";
import { useSavedFilterLifecycle } from "@/shared/composables/useSavedFilterLifecycle";
import { AdvancedFilterLibraryModal, SavedFilterLifecycleModals, } from "@/shared/components/advanced-filter-library";
import { useAppToast } from "@/shared/composables/useAppToast";
import { useListViewPreferences } from "@/shared/composables/useListViewPreferences";
import { useOrganisations } from "../../composables/useOrganisations";
import CreateOrganisationModal from "../components/CreateOrganisationModal.vue";
import { buildFilterSelectorLabels } from "@/shared/composables/filterSelectorLabels";
import { buildTableLabels } from "@/shared/composables/tableLabels";
import { useListRowSelection } from "@/shared/composables/useListRowSelection";
import ListFloatActionBar from "@/shared/components/ListFloatActionBar.vue";
import { handleNavigationFailure } from "@/shared/utils/navigation";
import { ORGANISATION_FILTER_VALUES } from "../../domain/organisationFilterValues";
const ORG_DEFAULT_PRESET_MAX = 5;
export default /*@__PURE__*/ _defineComponent({
    __name: 'OrganisationList',
    setup(__props) {
        const { t } = useI18n();
        const filterSelectorLabels = computed(() => buildFilterSelectorLabels(t));
        const { canEditOphub: canEdit, disabledTooltip } = usePermissions();
        const ROW_OPTIONS = [10, 30, 50];
        const showCreateModal = ref(false);
        // ── OP-6491 "Filtrer" entry point — real OPQRY field catalog ─────────────────
        const { toastError } = useAppToast();
        const filterCatalog = useQuickFilterCatalog(() => getFilterMetadata("organisation"), {
            rootGroupLabel: () => t("filters.groups.organisations"),
            options: ORGANISATION_FILTER_VALUES,
        });
        const { fields: filterFields, fieldGroups: filterFieldGroups, tableQueryOptions: filterTableQueryOptions, } = filterCatalog;
        // ── Table query & views ───────────────────────────────────────────────────────
        const tableQuery = useTableQuery({ pageSize: ROW_OPTIONS[0], ...filterTableQueryOptions });
        const { ready: quickFiltersReady, refinement: quickRefinement, onListFilterApply, loadErrorKey, } = useQuickFilters(tableQuery, filterCatalog, { storageKey: "organisation" });
        const tableViews = useTableViews({ initialViews: [] });
        // Seed the table search from the URL (?q=) so the header global-search "see all" link
        // deep-links into this list pre-filtered. `immediate` covers arriving fresh; the watcher
        // also re-seeds when a new ?q= is pushed while already on this page.
        const route = useRoute();
        const router = useRouter();
        watch(() => route.query.q, (q) => {
            const term = typeof q === "string" ? q : "";
            if (term && term !== tableQuery.state.search)
                tableQuery.state.search = term;
        }, { immediate: true });
        // Reopen the creation modal when the organisation fiche's back link sent us here
        // (dedup "Accéder à la fiche" flow, OP-7384) — see Organisation.vue's backLinkTo /
        // CreateOrganisationModal.goToOrganisation. The query is consumed once and stripped so a
        // refresh or browser back/forward doesn't keep reopening the modal.
        const reopenLegalName = ref("");
        // One-shot: clear as soon as the modal closes so a later, unrelated "+ Créer une
        // organisation" click (same page lifetime, modal never unmounts) doesn't reseed the
        // stale name from this reopen and re-fire a dedup lookup for it.
        watch(showCreateModal, (isOpen) => {
            if (!isOpen)
                reopenLegalName.value = "";
        });
        onMounted(() => {
            if (route.query.reopenCreateOrganisation !== "1")
                return;
            reopenLegalName.value = typeof route.query.legalName === "string" ? route.query.legalName : "";
            showCreateModal.value = true;
            const { reopenCreateOrganisation: _reopenCreateOrganisation, legalName: _legalName, ...rest } = route.query;
            void router.replace({ query: rest }).catch(handleNavigationFailure("OrganisationList:reopen-create"));
        });
        // ── Filters & per-user pinned pre-filtered lists ──────────────────────────────
        // `allLists` covers all scopes (eudonet / admin / myLists / shared); `filtersReady` gates restore.
        const { allLists, filtersReady } = useFilters("organisation", "ophub");
        // ── OP-6506 Advanced filter builder ───────────────────────────────────────────
        const advancedFilter = useAdvancedFilter("organisation");
        onMounted(() => {
            void advancedFilter.load();
        });
        // Anchors the OP-6869 popover under the "Filtrer" trigger instead of a centred modal.
        //
        // `anchorEl` is read from `filterSelectorRef` inside the `watch(builderOpen, ...)` below, not
        // reactively in the template or a `computed`: `filterSelectorRef` is written by Vue's own
        // `ref="filterSelectorRef"` mechanism as a side effect of the SAME render pass that mounts
        // `TableFilterSelector` (inside TableShell's filter slot). Making the RENDER EFFECT itself
        // depend on `filterSelectorRef` (a template read, or a `computed`/`watchEffect` that tracks
        // it) reliably corrupted the component tree in testing (observed as a crash deep in an
        // unrelated sibling during unmount) — it's concurrently written by the same pass that would
        // be reading it. A `watch(builderOpen, cb)` callback does NOT have this problem: only
        // `builderOpen` is a tracked dependency of the watch itself, so reading `filterSelectorRef.value`
        // inside the callback body is a plain, untracked property access — the same as reading it
        // inside a click handler — just centralized to one place instead of duplicated at every entry
        // point that can flip `builderOpen` to `true` (including `useSavedFilterLifecycle.onEditFilter`,
        // which sets it directly and has no `anchorEl` access of its own).
        const filterSelectorRef = ref(null);
        const anchorEl = ref(null);
        const advancedFilterPopoverRef = ref(null);
        const builderOpen = ref(false);
        const draftAst = ref(null);
        function openAdvancedBuilder() {
            draftAst.value = advancedFilter.appliedAst.value;
            builderOpen.value = true;
        }
        // `AdvancedFilterPopover` has no `@close` event — `update:open(false)` is its only close
        // signal, for every dismissal path (Escape, outside click, trigger re-click, and the
        // close-guard's confirmed "Fermer sans appliquer"). `lifecycle.closeBuilder()` is the single
        // "builder closed for any other reason than a successful apply/save" exit and already clears
        // `editingFilter` — apply/save keep closing directly via their own handlers below. The `open`
        // branch captures the trigger element (see the comment above `filterSelectorRef`) on every
        // entry point, not just `openAdvancedBuilder`/`openLibrary`.
        watch(builderOpen, (open) => {
            if (open) {
                anchorEl.value = filterSelectorRef.value?.triggerEl ?? null;
            }
            else {
                lifecycle.closeBuilder();
            }
        });
        function onApplyAdvanced(ast) {
            advancedFilter.applyAst(ast);
            // A real (non-empty) advanced filter takes precedence over an active saved view (useSearch
            // sends them as mutually exclusive on the wire) — deselect the view tab so the UI doesn't
            // show a saved view selected while ad-hoc results render.
            if (advancedFilter.appliedAst.value)
                tableQuery.setView(null);
            // Applying (instead of saving) must also leave edit mode — otherwise a stale
            // editingFilterId survives into the next builder session and a brand-new save
            // silently PUTs over the filter that was being edited (lifecycle.closeBuilder
            // clears both builderOpen and editingFilterId).
            lifecycle.closeBuilder();
        }
        // ── OP-6508 Advanced filter library ───────────────────────────────────────────
        const libraryOpen = ref(false);
        function openLibrary() {
            // Reachable from TWO independent triggers: the popover's own "view all" header link
            // (already routed through its internal requestClose/dirty-check before emitting view-all)
            // AND TableFilterSelector's own "Voir tous les filtres avancés" CTA — a SEPARATE control,
            // reachable even while the popover is open and dirty. Force-closing the popover here
            // (`builderOpen.value = false`) would silently discard unsaved criteria with no
            // confirmation on that second path. requestCloseThen() routes through the popover's own
            // guard instead — pristine closes silently, a dirty panel asks first — and only opens the
            // library once the panel has actually closed. It also leaves the popover "open" behind the
            // library modal if the user chooses to stay, which used to make
            // useSavedFilterLifecycle.onEditFilter's `if (... || builderOpen.value) return;` guard
            // silently discard the edit — a moot point now: if the user stays, the library never opens.
            const openLibraryNow = () => {
                libraryOpen.value = true;
            };
            if (advancedFilterPopoverRef.value) {
                advancedFilterPopoverRef.value.requestCloseThen(openLibraryNow);
            }
            else {
                builderOpen.value = false;
                openLibraryNow();
            }
        }
        // Apply a library filter server-side by its id (mutually exclusive with an inline ast).
        function onApplyLibraryFilter(id) {
            advancedFilter.clear();
            tableQuery.setView(id);
        }
        function onCreateFromLibrary() {
            openAdvancedBuilder();
        }
        // ── Saved-filter lifecycle (save / edit / rename / delete) ───────────────────
        const lifecycle = useSavedFilterLifecycle({
            entity: "organisation",
            application: "ophub",
            draftAst,
            builderOpen,
            advancedFilter: { appliedAst: advancedFilter.appliedAst, clear: () => advancedFilter.clear() },
            tableQuery,
            onError: toastError,
            // Forward-reference: `loadOrganisations` is declared further down (useOrganisations),
            // but this closure is only invoked later, on save — by then it is already assigned.
            reloadList: () => loadOrganisations(),
            // Every close this composable drives (closeBuilder, closeBuilderAndSelect via the
            // watch(builderOpen) above) bypasses the popover's own close()/builder.reset() — reset
            // its internal dirty state here too so the next open doesn't have to infer staleness.
            onBuilderClosed: () => advancedFilterPopoverRef.value?.discard(),
        });
        // Every user-visible string the OPAL builder needs (R29) — OPAL ships no copy.
        // Declared after `lifecycle` so the builder title can follow its editing state: the same
        // builder serves creation and edition, and `editingFilter` is what tells them apart —
        // its name is interpolated into the edition title (`filters.builder.editTitle`, OP-6885).
        const builderLabels = useAdvancedFilterLabels({
            editingName: () => lifecycle.editingFilter.value?.name,
        });
        // Pre-translated labels consumed by useListViewPreferences to build the tabDropdowns.
        const listLabels = computed(() => ({
            preFilteredLists: t("table.tabs.preFilteredLists"),
            eudonet: t("table.tabs.eudonet"),
            admin: t("table.tabs.admin"),
            myLists: t("table.tabs.myLists"),
            shared: t("table.tabs.sharedLists"),
            selected: t("table.tabs.selected"),
            searchPlaceholder: t("table.tabs.searchPlaceholder"),
            searchClearLabel: t("common.clearSearch"),
            lists: t("table.tabs.lists"),
            overflowTooltip: t("table.tabs.overflowTooltip"),
            createList: t("table.tabs.createList"),
            noListCreated: t("table.tabs.noListCreated"),
        }));
        // Organisation default tabs = eudonet-scope lists with displayOrder <= 5
        // (preserves today's behaviour where the five legalStatus preset tabs appeared by default).
        const defaultPinnedIds = computed(() => allLists.value.filter((l) => l.scope === "eudonet" && l.displayOrder <= ORG_DEFAULT_PRESET_MAX).map((l) => l.id));
        const { tabDropdowns, reorder, restore } = useListViewPreferences({
            entity: "organisation",
            tableQuery,
            tableViews,
            allLists,
            defaultPinnedIds,
            hasUrlView: () => !!route.query.view,
            labels: listLabels,
        });
        // Restore once filters have loaded (labels are needed to register pinned tabs as views).
        // Use `let` so the stop handle is available inside the immediate callback.
        let stopReady;
        stopReady = watch(filtersReady, (ready) => {
            if (!ready)
                return;
            void restore();
            stopReady?.();
        }, { immediate: true });
        // ── Column settings ───────────────────────────────────────────────────────────
        const columnSettings = useListSettings({
            entity: 'organisation',
            columns: () => [
                {
                    key: 'legalName',
                    label: t('organisationList.columns.legalName'),
                    visible: true,
                    pinned: true,
                    disabled: true,
                },
                {
                    key: 'tradeName',
                    label: t('organisationList.columns.tradeName'),
                    visible: true,
                    pinned: false,
                },
                {
                    key: 'organisationType',
                    label: t('organisationList.columns.organisationType'),
                    visible: true,
                    pinned: false,
                },
                // Opt-in columns (OP-5828) — hidden by default so existing layouts don't shift.
                { key: 'acronym', label: t('organisationList.columns.acronym'), visible: false, pinned: false },
                { key: 'industrySector', label: t('organisationList.columns.industrySector'), visible: false, pinned: false },
                { key: 'externalId', label: t('organisationList.columns.externalId'), visible: false, pinned: false },
                { key: 'registrationNumber', label: t('organisationList.columns.registrationNumber'), visible: false, pinned: false },
                { key: 'legalForm', label: t('organisationList.columns.legalForm'), visible: false, pinned: false },
                { key: 'establishmentNature', label: t('organisationList.columns.establishmentNature'), visible: false, pinned: false },
                { key: 'legalStatus', label: t('organisationList.columns.legalStatus'), visible: false, pinned: false },
                { key: 'employeeCount', label: t('organisationList.columns.employeeCount'), visible: false, pinned: false },
                { key: 'activityCode', label: t('organisationList.columns.activityCode'), visible: false, pinned: false },
                { key: 'registrationDate', label: t('organisationList.columns.registrationDate'), visible: false, pinned: false },
                { key: 'turnover', label: t('organisationList.columns.turnover'), visible: false, pinned: false },
                { key: 'createdAt', label: t('organisationList.columns.createdAt'), visible: false, pinned: false },
                { key: 'updatedAt', label: t('organisationList.columns.updatedAt'), visible: false, pinned: false },
            ],
        });
        // "select" (the row-checkbox column) is not user-managed: it leads the order and
        // the left-pinned cluster (tanstack honours the columnPinning.left order).
        const columnOrder = computed(() => ['select', ...columnSettings.order.value]);
        const columnPinning = computed(() => ({
            left: ['select', ...(columnSettings.pinning.value.left ?? []).filter((k) => k !== 'select')],
            right: columnSettings.pinning.value.right ?? [],
        }));
        // Restore saved sort after settings finish loading (OP-6130).
        // Must be registered BEFORE useOrganisations so this watcher runs first when
        // isLoading goes false, ensuring tableQuery.state.sort is set before useSearch fires.
        watch(columnSettings.isLoading, (loading) => {
            if (!loading && columnSettings.activeSort.value) {
                const { column, direction } = columnSettings.activeSort.value;
                tableQuery.setSort(column, direction);
            }
        });
        // Clear sort when the user resets column settings (OP-6130).
        watch(columnSettings.activeSort, (sort) => {
            if (!columnSettings.isLoading.value && sort === null) {
                tableQuery.clearSort();
            }
        });
        // ── Data loading ──────────────────────────────────────────────────────────────
        // Gate on settings-loaded and on the restored chips too, so the first fetch already carries the sort and the chips.
        const dataReady = computed(() => filtersReady.value && !columnSettings.isLoading.value && quickFiltersReady.value);
        const { organisations, pageResponse, loading, loadError, loadErrorCode, reload: loadOrganisations } = useOrganisations(tableQuery, dataReady, advancedFilter.appliedAst, quickRefinement);
        const loadErrorTitle = computed(() => t(loadErrorKey(loadErrorCode.value, "organisationList.loadError")));
        // ── Row selection + float action bar ─────────────────────────────────────────
        const { selectColumn, effectiveCount, allPagesSelected, totalAcrossAllPages, clearSelection, onSelectAllPages } = useListRowSelection({
            t,
            rows: () => organisations.value ?? [],
            getRowId: (organisation) => organisation.id,
            totalCount: () => pageResponse.value?.totalItems,
        });
        watch([organisations, pageResponse], ([list, page]) => {
            if (!page)
                return;
            saveListNavContext('organisation', (list ?? []).map((o) => o.id), {
                query: {
                    filterId: advancedFilter.appliedAst.value ? null : tableQuery.state.activeViewId ?? undefined,
                    ast: advancedFilter.appliedAst.value ?? undefined,
                    refinement: quickRefinement.value ?? undefined,
                    search: tableQuery.state.search || undefined,
                    page: page.currentPage,
                    pageSize: page.pageSize,
                    sortBy: tableQuery.state.sort?.column,
                    sortOrder: tableQuery.state.sort?.direction,
                },
                pagination: {
                    currentPage: page.currentPage,
                    totalPages: page.totalPages,
                },
            });
        }, { immediate: true });
        function onOrganisationCreated() {
            if (tableQuery.state.page === 1) {
                loadOrganisations();
            }
            else {
                tableQuery.setPage(1);
            }
        }
        // Activate the first selected item as the current view when the user picks a tab.
        function onTabRequested(_dropdownIndex, selectedItems) {
            const first = selectedItems[0];
            if (first) {
                // A saved view takes precedence over an active advanced filter — mirrors onApplyAdvanced's
                // reverse deselection so the two mechanisms stay mutually exclusive both ways. Without this,
                // useSearch keeps forcing filterId: null (an ast is still applied) and the tab looks inert.
                advancedFilter.clear();
                tableQuery.setView(first.id);
            }
        }
        // Persist the new drag-reorder for the given dropdown's segment.
        function onTabReordered(_dropdownIndex, orderedIds) {
            reorder(orderedIds);
        }
        // ── TableShell labels ─────────────────────────────────────────────────────────
        const tableLabels = computed(() => buildTableLabels(t));
        // ── Sort helpers ──────────────────────────────────────────────────────────────
        const sortItems = (key) => [
            { label: t('table.sortAZ'), onClick: () => {
                    tableQuery.setSort(key, 'asc');
                    columnSettings.handleSortChange({ column: key, direction: 'asc' });
                } },
            { label: t('table.sortZA'), onClick: () => {
                    tableQuery.setSort(key, 'desc');
                    columnSettings.handleSortChange({ column: key, direction: 'desc' });
                } },
        ];
        // ── Column helpers (shared with Contact via useListColumns) ─────────────────────
        const { formatDate, tOrRaw, hideColumn, optionalColumn } = useListColumns({
            headerNamespace: 'organisationList.columns',
            columnSettings,
        });
        // ── Columns ───────────────────────────────────────────────────────────────────
        const organisationColumns = [
            {
                id: 'legalName',
                header: t('organisationList.columns.legalName'),
                cell: ({ row }) => h('div', { class: 'flex items-center gap-2' }, [
                    h(Avatar, {
                        name: row.original.mainInformation.legalName,
                        img: row.original.avatarUrl ?? undefined,
                        size: 'md',
                        entity: 'organisation',
                        logo: true,
                        presentational: true,
                    }),
                    h(RouterLink, {
                        to: { name: 'Organisation', params: { id: row.original.id } },
                        class: 'text-blue-600 hover:underline',
                        'data-testid': `organisation-row-link-${row.original.id}`,
                    }, () => row.original.mainInformation.legalName),
                ]),
                draggable: false,
                headerMenuItems: [...sortItems('legalName')],
            },
            {
                id: 'tradeName',
                header: t('organisationList.columns.tradeName'),
                cell: ({ row }) => row.original.mainInformation.tradeName ?? '–',
                draggable: true,
                headerMenuItems: [
                    ...sortItems('tradeName'),
                    { label: t('table.hideColumn'), onClick: () => hideColumn('tradeName') },
                ],
            },
            {
                id: 'organisationType',
                header: t('organisationList.columns.organisationType'),
                cell: ({ row }) => row.original.mainInformation.organisationType || '–',
                draggable: true,
                headerMenuItems: [
                    ...sortItems('organisationType'),
                    { label: t('table.hideColumn'), onClick: () => hideColumn('organisationType') },
                ],
            },
            optionalColumn('acronym', ({ row }) => row.original.mainInformation.acronym || '–'),
            optionalColumn('industrySector', ({ row }) => {
                const sector = row.original.mainInformation.industrySector;
                if (!sector)
                    return '–';
                // Only the "Autre" fallback is i18n-mapped; known sectors are catalogue display text.
                return sector === INDUSTRY_SECTOR_FALLBACK
                    ? t('organisation.detail.options.industrySector.other')
                    : sector;
            }),
            optionalColumn('externalId', ({ row }) => row.original.externalId || '–'),
            optionalColumn('registrationNumber', ({ row }) => row.original.administrativeInformation.siret || '–'),
            optionalColumn('legalForm', ({ row }) => row.original.administrativeInformation.legalForm || '–'),
            optionalColumn('establishmentNature', ({ row }) => {
                const value = row.original.administrativeInformation.establishmentNature;
                return value ? tOrRaw(`organisation.detail.options.establishmentNature.${value}`, value) : '–';
            }),
            optionalColumn('legalStatus', ({ row }) => {
                const value = row.original.administrativeInformation.legalStatus;
                return value ? tOrRaw(`organisation.legalStatus.${value}`, value) : '–';
            }),
            optionalColumn('employeeCount', ({ row }) => {
                const count = row.original.administrativeInformation.headcount;
                return count != null ? String(count) : '–';
            }),
            optionalColumn('activityCode', ({ row }) => row.original.administrativeInformation.activityCode || '–'),
            optionalColumn('registrationDate', ({ row }) => formatDate(row.original.administrativeInformation.registrationDate)),
            optionalColumn('turnover', ({ row }) => {
                const value = row.original.administrativeInformation.turnover;
                return value != null ? String(value) : '–';
            }),
            optionalColumn('createdAt', ({ row }) => formatDate(row.original.createdAt)),
            optionalColumn('updatedAt', ({ row }) => formatDate(row.original.updatedAt)),
        ];
        const columns = computed(() => [selectColumn, ...organisationColumns]);
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createElementVNode("div", _hoisted_1, [
                    _createElementVNode("div", _hoisted_2, [
                        _createElementVNode("h1", _hoisted_3, _toDisplayString(_ctx.$t("organisationList.title")), 1 /* TEXT */),
                        _createElementVNode("div", _hoisted_4, [
                            _createElementVNode("div", _hoisted_5, [
                                _createVNode(_unref(Tooltip), {
                                    text: _unref(disabledTooltip),
                                    disabled: _unref(canEdit)
                                }, {
                                    default: _withCtx(() => [
                                        _createVNode(_unref(Button), {
                                            label: _ctx.$t('organisationList.create'),
                                            "icon-left": "ri-add-fill",
                                            disabled: !_unref(canEdit),
                                            "data-testid": "organisation-create-button",
                                            onClick: _cache[0] || (_cache[0] = ($event) => (showCreateModal.value = true))
                                        }, null, 8 /* PROPS */, ["label", "disabled"])
                                    ]),
                                    _: 1 /* STABLE */
                                }, 8 /* PROPS */, ["text", "disabled"])
                            ])
                        ])
                    ]),
                    _createVNode(_unref(TableShell), {
                        query: _unref(tableQuery),
                        views: _unref(tableViews),
                        labels: tableLabels.value,
                        permissions: { canSaveView: false, canShareView: false },
                        "tab-dropdowns": _unref(tabDropdowns),
                        "column-settings": _unref(columnSettings),
                        "show-view-selector": true,
                        onTabRequested: onTabRequested,
                        onTabReordered: onTabReordered,
                        onEditFilter: _cache[4] || (_cache[4] = (key) => filterSelectorRef.value?.openForField(key))
                    }, {
                        filter: _withCtx(() => [
                            _createVNode(_unref(TableFilterSelector), _mergeProps({
                                ref_key: "filterSelectorRef",
                                ref: filterSelectorRef
                            }, filterSelectorLabels.value, {
                                fields: _unref(filterFields),
                                "field-groups": _unref(filterFieldGroups),
                                "active-count": _unref(tableQuery).activeFilterCount.value,
                                "trigger-label": _ctx.$t('filters.button'),
                                "trigger-icon-left": "ri-filter-2-line",
                                "step1-search-placeholder": _ctx.$t('filters.searchPlaceholder'),
                                "step1-footer-label": _ctx.$t('filters.footerLabel'),
                                "view-advanced-label": _ctx.$t('filters.viewAdvanced'),
                                "create-advanced-label": _ctx.$t('filters.createAdvanced'),
                                "advanced-disabled": _unref(advancedFilter).isDisabled.value,
                                "advanced-disabled-tooltip": _ctx.$t('filters.comingSoon'),
                                "no-fields-label": _ctx.$t('filters.noFields'),
                                "fields-selectable": true,
                                "active-filters": _unref(tableQuery).state.filters ?? {},
                                "confirm-label": _ctx.$t('table.tabs.apply'),
                                "back-label": _ctx.$t('filters.quick.back'),
                                "no-results-label": _ctx.$t('filters.quick.noValueResults'),
                                "date-range-error-label": _ctx.$t('filters.quick.dateRangeError'),
                                onCreateAdvancedFilter: openAdvancedBuilder,
                                onViewAdvancedFilters: openLibrary,
                                onApply: _unref(onListFilterApply),
                                onApplyFilter: _cache[1] || (_cache[1] = (key, value) => _unref(tableQuery).setFilter(key, value)),
                                onRemoveFilter: _cache[2] || (_cache[2] = (key) => _unref(tableQuery).removeFilter(key))
                            }), null, 16 /* FULL_PROPS */, ["fields", "field-groups", "active-count", "trigger-label", "step1-search-placeholder", "step1-footer-label", "view-advanced-label", "create-advanced-label", "advanced-disabled", "advanced-disabled-tooltip", "no-fields-label", "active-filters", "confirm-label", "back-label", "no-results-label", "date-range-error-label", "onApply"])
                        ]),
                        table: _withCtx(() => [
                            (_unref(loading) || _unref(loadError) || _unref(organisations).length === 0)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                    (_unref(loading))
                                        ? (_openBlock(), _createBlock(ModuleLoading, { key: 0 }))
                                        : (_unref(loadError))
                                            ? (_openBlock(), _createBlock(ModuleState, {
                                                key: 1,
                                                type: "error",
                                                class: "flex-1",
                                                title: loadErrorTitle.value,
                                                "cta-icon": "ri-refresh-line",
                                                "cta-label": _unref(t)('common.retry'),
                                                onCta: _unref(loadOrganisations)
                                            }, null, 8 /* PROPS */, ["title", "cta-label", "onCta"]))
                                            : (_openBlock(), _createBlock(ModuleState, {
                                                key: 2,
                                                type: "empty",
                                                class: "flex-1",
                                                title: _unref(t)('organisationList.empty'),
                                                "cta-icon": "ri-add-fill",
                                                "cta-label": _unref(t)('organisationList.create'),
                                                "cta-disabled": !_unref(canEdit),
                                                "cta-disabled-tooltip": _unref(disabledTooltip),
                                                onCta: _cache[3] || (_cache[3] = ($event) => (showCreateModal.value = true))
                                            }, null, 8 /* PROPS */, ["title", "cta-label", "cta-disabled", "cta-disabled-tooltip"]))
                                ]))
                                : (_openBlock(), _createBlock(_unref(DataTable), {
                                    key: 1,
                                    columns: columns.value,
                                    data: _unref(organisations),
                                    "column-visibility": _unref(columnSettings).visibility,
                                    "column-order": columnOrder.value,
                                    "column-pinning": columnPinning.value,
                                    "column-sizing": _unref(columnSettings).widths.value,
                                    "has-settings": "",
                                    "sticky-header": "",
                                    onSettingsClick: _unref(columnSettings).openDrawer,
                                    "onUpdate:columnSizing": _unref(columnSettings).handleSizingChange
                                }, null, 8 /* PROPS */, ["columns", "data", "column-visibility", "column-order", "column-pinning", "column-sizing", "onSettingsClick", "onUpdate:columnSizing"]))
                        ]),
                        pagination: _withCtx(() => [
                            _createVNode(_unref(Pager), {
                                "current-page": _unref(pageResponse)?.currentPage ?? _unref(tableQuery).state.page,
                                "total-pages": _unref(pageResponse)?.totalPages ?? 0,
                                "total-items": _unref(pageResponse)?.totalItems ?? 0,
                                "items-per-page": _unref(tableQuery).state.pageSize,
                                "rows-per-page-options": ROW_OPTIONS,
                                "rows-per-page-label": _ctx.$t('pagination.rowsPerPage'),
                                "items-count-separator": _ctx.$t('pagination.of'),
                                "previous-label": _ctx.$t('pagination.previous'),
                                "next-label": _ctx.$t('pagination.next'),
                                "show-go-to-page": "",
                                "go-to-page-label": _ctx.$t('pagination.goToPage'),
                                "go-label": _ctx.$t('pagination.go'),
                                onPageChange: _unref(tableQuery).setPage,
                                onItemsPerPageChange: _unref(tableQuery).setPageSize
                            }, null, 8 /* PROPS */, ["current-page", "total-pages", "total-items", "items-per-page", "rows-per-page-label", "items-count-separator", "previous-label", "next-label", "go-to-page-label", "go-label", "onPageChange", "onItemsPerPageChange"])
                        ]),
                        _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["query", "views", "labels", "tab-dropdowns", "column-settings"]),
                    _createVNode(ListFloatActionBar, {
                        "selection-count": _unref(effectiveCount),
                        "all-pages-selected": _unref(allPagesSelected),
                        "total-across-all-pages": _unref(totalAcrossAllPages),
                        onDismiss: _unref(clearSelection),
                        onSelectAllPages: _unref(onSelectAllPages)
                    }, null, 8 /* PROPS */, ["selection-count", "all-pages-selected", "total-across-all-pages", "onDismiss", "onSelectAllPages"]),
                    _createVNode(CreateOrganisationModal, {
                        modelValue: showCreateModal.value,
                        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((showCreateModal).value = $event)),
                        "initial-name": reopenLegalName.value,
                        onCreated: onOrganisationCreated
                    }, null, 8 /* PROPS */, ["modelValue", "initial-name"])
                ]),
                _createVNode(_unref(AdvancedFilterPopover), {
                    ref_key: "advancedFilterPopoverRef",
                    ref: advancedFilterPopoverRef,
                    open: builderOpen.value,
                    "onUpdate:open": _cache[6] || (_cache[6] = ($event) => ((builderOpen).value = $event)),
                    modelValue: draftAst.value,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => ((draftAst).value = $event)),
                    "anchor-el": anchorEl.value,
                    fields: _unref(advancedFilter).builderFields.value,
                    "field-groups": _unref(advancedFilter).fieldGroups.value,
                    labels: _unref(builderLabels),
                    busy: _unref(lifecycle).savePending.value,
                    "show-save": "",
                    onApply: onApplyAdvanced,
                    onSave: _unref(lifecycle).onBuilderSave,
                    onViewAll: openLibrary
                }, null, 8 /* PROPS */, ["open", "modelValue", "anchor-el", "fields", "field-groups", "labels", "busy", "onSave"]),
                _createVNode(_unref(AdvancedFilterLibraryModal), {
                    modelValue: libraryOpen.value,
                    "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event) => ((libraryOpen).value = $event)),
                    entity: "organisation",
                    application: "ophub",
                    "builder-fields": _unref(advancedFilter).builderFields.value,
                    onApply: onApplyLibraryFilter,
                    onCreate: onCreateFromLibrary,
                    onEdit: _unref(lifecycle).onEditFilter,
                    onRename: _unref(lifecycle).onRenameFilter,
                    onDelete: _unref(lifecycle).onDeleteFilter,
                    onManageAccess: _unref(lifecycle).onManageAccess
                }, null, 8 /* PROPS */, ["modelValue", "builder-fields", "onEdit", "onRename", "onDelete", "onManageAccess"]),
                _createVNode(_unref(SavedFilterLifecycleModals), { lifecycle: _unref(lifecycle) }, null, 8 /* PROPS */, ["lifecycle"])
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
